import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, MapPin, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { CATEGORIES, COUNTRIES, CURRENCIES } from '../data/constants';
import { useToast } from '../hooks/use-toast';
import { api, apiError } from '../lib/api';
import { startCheckout, LOOKUP_KEYS } from '../lib/payments';
import { PhotoUploader, ToggleOption } from '../components/post/PhotoUploader';
import { AiWriter } from '../components/post/AiWriter';

const Field = ({ label, children, full = false }) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <label className="block text-sm font-bold text-[#1A1A1A] mb-2">{label}</label>
    {children}
  </div>
);

const CONDITIONS = ['new', 'like-new', 'used', 'service'];

const PostAd = () => {
  const { t, lang } = useApp();
  const { user } = useAuth();
  const { toast } = useToast();
  const nav = useNavigate();
  const [form, setForm] = useState({ title: '', cat: '', price: '', currency: 'XOF', desc: '', country: user?.country || 'SN', city: '', condition: 'new', diaspora: false, boost: false });
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.cat) { toast({ title: t('post.chooseCat') }); return; }
    setSaving(true);
    const countryName = COUNTRIES.find((c) => c.code === form.country)?.name || form.country;
    try {
      const { data: listing } = await api.post('/listings', {
        title: form.title, description: form.desc, category: form.cat, price: Number(form.price), currency: form.currency,
        country: form.country, location: form.city ? `${form.city}, ${countryName}` : countryName, images: photos,
        condition: form.condition, diasporaShipping: form.diaspora,
      });
      if (form.boost) {
        toast({ title: t('post.published'), description: t('post.redirectBoost') });
        await startCheckout(LOOKUP_KEYS.boost, { userId: user.id, listingId: listing.id });
        return;
      }
      toast({ title: t('post.published'), description: t('post.publishedDesc') });
      nav(`/listing/${listing.id}`);
    } catch (err) {
      console.error('Publish failed', err);
      toast({ title: t('post.createError'), description: apiError(err) });
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="post-ad-page">
      <div className="text-center mb-10">
        <h1 className="text-3xl md:text-4xl font-black tracking-tight text-[#1A1A1A]">{t('post.title')}</h1>
        <p className="text-[#7A7468] mt-2">{t('post.subtitle')}</p>
      </div>
      <form onSubmit={submit} className="bg-white rounded-2xl border border-[#EFEAE0] p-6 md:p-10 space-y-8" data-testid="post-ad-form">
        <PhotoUploader photos={photos} setPhotos={setPhotos} />
        <AiWriter context={{ category: form.cat || undefined, condition: form.condition, location: form.city || undefined }} onApply={(c) => set({ title: c.title, desc: c.description })} />
        <div className="grid md:grid-cols-2 gap-5">
          <Field label={t('post.name')} full>
            <Input value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder={t('post.titlePlaceholder')} required minLength={3} className="h-11" data-testid="post-title-input" />
          </Field>
          <Field label={t('post.cat')}>
            <Select value={form.cat} onValueChange={(cat) => set({ cat })}>
              <SelectTrigger className="h-11" data-testid="post-category-select"><SelectValue placeholder={t('post.chooseCat')} /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`post-category-${c.id}`}>{c.name[lang] || c.name.en}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t('mkt.condition')}>
            <Select value={form.condition} onValueChange={(condition) => set({ condition })}>
              <SelectTrigger className="h-11" data-testid="post-condition-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c} value={c}>{t(`cond.${c}`)}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={`${t('post.country')} / ${t('post.location')}`}>
            <div className="flex gap-2">
              <Select value={form.country} onValueChange={(country) => set({ country })}>
                <SelectTrigger className="w-28 h-11" data-testid="post-country-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.flag} {c.code}</SelectItem>)}</SelectContent>
              </Select>
              <Input value={form.city} onChange={(e) => set({ city: e.target.value })} placeholder={t('post.city')} className="h-11" data-testid="post-city-input" />
            </div>
          </Field>
          <Field label={t('post.price')}>
            <Input type="number" min="0" value={form.price} onChange={(e) => set({ price: e.target.value })} placeholder="0" required className="h-11" data-testid="post-price-input" />
          </Field>
          <Field label={t('post.currency')}>
            <Select value={form.currency} onValueChange={(currency) => set({ currency })}>
              <SelectTrigger className="h-11" data-testid="post-currency-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.keys(CURRENCIES).map((c) => <SelectItem key={c} value={c}>{CURRENCIES[c].symbol} {c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t('post.desc')} full>
            <Textarea value={form.desc} onChange={(e) => set({ desc: e.target.value })} placeholder={t('post.descPlaceholder')} rows={5} required minLength={10} data-testid="post-desc-input" />
          </Field>
        </div>
        <div className="space-y-3">
          <ToggleOption icon={MapPin} title={t('post.diaspora')} hint={t('post.diasporaHint')} checked={form.diaspora} onChange={(diaspora) => set({ diaspora })} testId="post-diaspora-switch" />
          <ToggleOption icon={Zap} title={`${t('post.boost')} · 2 500 CFA`} hint={t('post.boostHint')} checked={form.boost} onChange={(boost) => set({ boost })} accent testId="post-boost-switch" />
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button type="submit" size="lg" disabled={saving} className="flex-1 bg-[#0F8B5C] hover:bg-[#0A6E47] text-white h-12 font-bold" data-testid="post-submit-button">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} {saving ? t('post.creating') : t('post.publish')}
          </Button>
          <Button type="button" variant="outline" size="lg" className="h-12" onClick={() => nav('/')} data-testid="post-cancel-button">{t('post.cancel')}</Button>
        </div>
      </form>
    </div>
  );
};

export default PostAd;
